import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import React, { useState, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { FaMapMarker } from 'react-icons/fa';
import { RiImageCircleLine, RiCloseLine, RiExternalLinkLine } from 'react-icons/ri';
import { useSelector } from 'react-redux';

import styles from './useIoTHubEvent.module.scss';

import RetryImage from '@/components/ui/RetryImage';
import OlMap from '@/helpers/OlMap';
import EventEmitter from '@/libs/EventEmitter';
import { rotate } from '@/utils/ImageUtils';
import { getElevation } from '@/utils/MapUtils';

const cx = classNames.bind(styles);

const MarkerContent = ({ data, color }) => {
  const [isShowInfo, setShowInfo] = useState(false);

  const toggle = () => {
    setShowInfo(!isShowInfo);
  };

  return (
    <div className={cx('container')}>
      {isShowInfo && (
        <div className={cx('panel')}>
          <div className={cx('line')} />
          <div className={cx('header')}>
            <div className={cx('time')}>{dayjs(data.capturedAt).format('HH:mm:ss')}</div>
            <div className={cx('close')} onClick={toggle}>
              <RiCloseLine size={14} color="white" />
            </div>
          </div>
          <a href={data.originUrl} target="_blank" rel="noopener noreferrer" className={cx('link')}>
            <RetryImage src={data.thumbnailUrl} alt={data.capturedAt} />
            <div className={cx('overlay')}>
              <RiExternalLinkLine size={16} color="white" />
            </div>
          </a>
        </div>
      )}
      <div className={cx('marker')} onClick={toggle}>
        <FaMapMarker size={24} color={color} />
        <RiImageCircleLine size={12} color="white" className={cx('icon')} />
      </div>
    </div>
  );
};

const useIoTHubEvent = (robot) => {
  const robotOptionsPhoto = useSelector((state) => state.robotOptions[robot.id].photo);
  const robotOptionsPhotoRef = useRef(robotOptionsPhoto);

  useMountEffect(() => {
    const token = EventEmitter.subscribe(`${robot.id}/event/showPhoto`, async (data) => {
      const element = getMarkerElement(data, robot.color);
      const position = {
        lat: data.position.latitude,
        lng: data.position.longitude,
      };

      //! footprint 임시 비활성화
      // const url = await rotate(data.thumbnailUrl, data.position.heading);
      const url = null;
      const aglAltitude = data.position.altitude - (await getElevation(position));

      OlMap.addPhoto(
        robot.id,
        data.imageId,
        element,
        position,
        url,
        aglAltitude,
        robotOptionsPhotoRef.current.focalLength,
        robotOptionsPhotoRef.current.camera.specs.sensorSize,
        robotOptionsPhotoRef.current.visible,
        robotOptionsPhotoRef.current.type
      );
    });

    return () => {
      EventEmitter.unsubscribe(token);
      OlMap.removePhotos(robot.id);
    };
  });

  useUpdateEffect(() => {
    robotOptionsPhotoRef.current = robotOptionsPhoto;
  }, [robotOptionsPhoto]);

  useUpdateEffect(() => {
    OlMap.setPhotosVisible(robot.id, robotOptionsPhoto.visible);
  }, [robotOptionsPhoto.visible]);

  useUpdateEffect(() => {
    OlMap.setPhotoType(robot.id, robotOptionsPhoto.type);
  }, [robotOptionsPhoto.type]);

  useUpdateEffect(() => {
    OlMap.resizePhotos(robot.id, robotOptionsPhoto.focalLength, robotOptionsPhoto.camera.specs.sensorSize);
  }, [robotOptionsPhoto.camera, robotOptionsPhoto.focalLength]);

  const getMarkerElement = (data, color) => {
    const content = document.createElement('div');
    createRoot(content).render(<MarkerContent data={data} color={color} />);
    return content;
  };
};

export default useIoTHubEvent;
